// "Add a friend" as an animated bottom sheet rather than a full page. Tapping
// the Cercle FAB slides this up over the list; adding closes it and refreshes
// the list in place, so the whole flow stays on one screen and feels light.
//
// Built as a transient overlay appended to <body> (same pattern as the scanner
// and toast), so it's independent of the router.

import { h } from './dom';
import { toast } from './toast';
import { currentIdentity } from '../core/identity';
import { getFriend, listFriends } from '../core/friends';
import { decodeCircleId } from '../core/keys';
import { addFriendAndSync } from '../core/sync';
import { parseInviteId } from './components';
import { scanQrInline, scanQrSupported, type InlineScan } from './scanner';
import { icon } from './icons';

/** Whether text decodes to a plausible Circle ID (raw or from an invite link). */
function looksLikeCircleId(text: string): boolean {
  try {
    decodeCircleId(parseInviteId(text));
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether this Circle ID is already in the address book. The first time we
 * ever see someone's ID there is always something to decide — at least
 * whether to give them a temporary name — so that pass always goes through
 * the sheet and its button. Once they're already a friend, a QR rescan, a
 * reopened invite link, or a repasted ID has nothing left to name: it can
 * only be the reciprocal confirmation (or a harmless repeat), so it can go
 * straight through.
 */
async function isKnownFriend(raw: string): Promise<boolean> {
  if (!looksLikeCircleId(raw)) return false;
  return (await getFriend(parseInviteId(raw))) !== undefined;
}

/**
 * Open the add-friend sheet. `prefill` seeds the ID field (used when
 * resuming from a shared invite link); `onAdded` runs after a successful add so
 * the caller can refresh its list.
 */
export function openAddFriendSheet(prefill = '', onAdded?: () => void): void {
  const idInput = h('textarea', {
    rows: '3',
    placeholder: 'Collez l’ID Cercle de votre proche',
    autocapitalize: 'none',
    autocomplete: 'off',
    spellcheck: 'false',
  }) as HTMLTextAreaElement;
  if (prefill) idInput.value = prefill;

  // Guards against double-submits: a scan result and a paste event could both
  // fire, or the button could be tapped while an auto-submit is in flight.
  let submitting = false;

  // Optional local placeholder ("Mamie", "Collègue du bureau"…) shown instead
  // of the raw ID stub until this friend syncs back and shares their real
  // name — never sent anywhere, purely to tell pending contacts apart.
  const nameInput = h('input', {
    type: 'text',
    placeholder: 'Ex. Mamie (facultatif)',
    maxlength: '60',
    autocapitalize: 'words',
    autocomplete: 'off',
    spellcheck: 'false',
  }) as HTMLInputElement;

  const fields: HTMLElement[] = [
    h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: 'ID Cercle' }), idInput),
    h(
      'label',
      { class: 'form-field' },
      h('span', { class: 'field-label', text: 'Nom temporaire (facultatif)' }),
      nameInput,
    ),
  ];

  // Inline camera: a live preview window mounted straight into the sheet, no
  // separate "scan" step — opening the sheet opens the camera. A scanned ID
  // just fills the field, same as pasting: submitting is still up to the
  // button (or automatic below, only once this person is already a friend).
  let scan: InlineScan | null = null;
  const scanPanel = h('div', { class: 'scan-inline' });

  const stopScan = (): void => {
    scan?.stop();
    scan = null;
    scanPanel.classList.remove('is-active');
    scanPanel.replaceChildren();
  };
  const startScan = (): void => {
    scanPanel.classList.add('is-active');
    const s = scanQrInline(scanPanel);
    scanPanel.append(h('div', { class: 'scan-inline-frame' }), h('div', { class: 'scan-inline-hint', text: 'Cadrez le QR code' }));
    scan = s;
    s.result
      .then(async (val) => {
        const circleId = parseInviteId(val);
        idInput.value = circleId;
        stopScan();
        if (await isKnownFriend(circleId)) void submit(circleId);
        else toast('QR code détecté');
      })
      .catch((e) => {
        const msg = (e as Error).message;
        stopScan();
        if (msg !== 'Scan arrêté') toast(msg);
      });
  };

  // Camera above the fields: it is the primary way in, and it starts on its
  // own as the sheet opens, so it should not sit below two inputs.
  const controls: HTMLElement[] = scanQrSupported() ? [scanPanel, ...fields] : fields;

  const addBtn = h('button', { class: 'btn btn-primary' }) as HTMLButtonElement;
  addBtn.append(icon('plus', 18), document.createTextNode('Ajouter à mon Cercle'));

  // Default lead: short, for anyone who's already added someone before. If
  // this turns out to be their very first add (checked below), it's swapped
  // for the three steps of the exchange — never the other way around, so
  // repeat use never gets cluttered with guidance it no longer needs.
  //
  // The steps are one terse line each, not the illustrated `bullet()` rows
  // used on the empty Cercle screen: this sheet has to leave room for the
  // camera panel below them.
  const leadSlot = h(
    'div',
    {},
    h('p', {
      class: 'lead',
      text:
        'Chacun ajoute l’autre : vous ajoutez son ID Cercle, il ajoute le vôtre. Tant qu’il manque un des deux, aucune coordonnée ne circule.',
    }),
  );
  void listFriends().then((friends) => {
    if (friends.length > 0) return;
    leadSlot.replaceChildren(
      h(
        'ol',
        { class: 'sheet-steps' },
        h('li', { text: 'Scannez son QR code, ou collez son ID Cercle.' }),
        h('li', { text: 'Envoyez-lui le vôtre depuis l’onglet Partager.' }),
        h('li', { text: 'Chacun doit ajouter l’autre, sinon rien ne circule.' }),
      ),
    );
  });

  const scrim = h('div', { class: 'sheet-scrim' });
  const handle = h('div', { class: 'sheet-handle' });
  const title = h('h2', { class: 'sheet-title', text: 'Ajouter un proche' });
  const sheet = h(
    'div',
    { class: 'sheet', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Ajouter un proche' },
    handle,
    title,
    leadSlot,
    ...controls,
    addBtn,
  );
  const overlay = h('div', { class: 'sheet-overlay' }, scrim, sheet);

  let closing = false;
  const close = (): void => {
    if (closing) return;
    closing = true;
    stopScan(); // release the camera if a scan is in progress
    overlay.classList.remove('is-open');
    document.removeEventListener('keydown', onKey);
    // Wait for the slide-down transition before removing from the DOM.
    setTimeout(() => overlay.remove(), 320);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };

  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  /**
   * Add the person and, in the same action, share my profile with them and
   * pull theirs if it's already waiting. Runs either from the button (a
   * fresh contact, with whatever name was typed in) or automatically once
   * this Circle ID is already a known friend (see `isKnownFriend`) — in that
   * case re-submitting is a harmless confirmation rather than a new add, so
   * it's treated as success even if the underlying add call itself no-ops.
   */
  const submit = async (raw: string): Promise<void> => {
    if (submitting) return;
    submitting = true;
    addBtn.disabled = true;
    try {
      const circleId = parseInviteId(raw);
      const me = currentIdentity();
      const { skipped, relayError } = await addFriendAndSync(circleId, nameInput.value, me);
      toast('Proche ajouté');
      if (relayError) toast('Serveur injoignable — l’envoi se fera tout seul plus tard.');
      else if (skipped === 'empty-profile') toast('Complétez votre fiche pour partager vos coordonnées');
      else if (skipped === 'relay-off') toast('Envoi impossible : aucun serveur configuré');
      close();
      onAdded?.();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg === 'Cette personne est déjà dans votre Cercle') {
        toast('Déjà dans votre Cercle — connexion à jour');
        close();
        onAdded?.();
      } else {
        toast(msg);
      }
    } finally {
      submitting = false;
      addBtn.disabled = false;
    }
  };

  // A pasted ID (or invite link) only skips the button when it's already a
  // known friend — the reciprocal-confirmation case with nothing left to
  // name. A brand-new ID just sits in the field for the name + button step.
  idInput.addEventListener('paste', () => {
    setTimeout(() => {
      void (async () => {
        const value = idInput.value;
        if (await isKnownFriend(value)) void submit(parseInviteId(value));
      })();
    }, 0);
  });

  // Drag-to-dismiss: grab the handle or title and pull the sheet down. Below
  // the distance/velocity threshold it springs back via the sheet's own CSS
  // transition; past it, the drag hands off into the normal close animation.
  const DRAG_CLOSE_DISTANCE = 120; // px
  const DRAG_CLOSE_VELOCITY = 0.5; // px/ms
  let dragStartY = 0;
  let dragY = 0;
  let dragStartTime = 0;
  let dragging = false;

  const onDragStart = (e: TouchEvent): void => {
    if (closing || e.touches.length !== 1) return;
    dragStartY = e.touches[0]!.clientY;
    dragY = dragStartY;
    dragStartTime = Date.now();
    dragging = true;
    sheet.classList.add('is-dragging');
  };
  const onDragMove = (e: TouchEvent): void => {
    if (!dragging) return;
    dragY = e.touches[0]!.clientY;
    e.preventDefault();
    sheet.style.transform = `translateY(${Math.max(0, dragY - dragStartY)}px)`;
  };
  const onDragEnd = (): void => {
    if (!dragging) return;
    dragging = false;
    sheet.classList.remove('is-dragging');
    sheet.style.transform = '';
    const dy = Math.max(0, dragY - dragStartY);
    const velocity = dy / Math.max(1, Date.now() - dragStartTime);
    if (dy > DRAG_CLOSE_DISTANCE || velocity > DRAG_CLOSE_VELOCITY) close();
  };

  for (const el of [handle, title]) {
    el.addEventListener('touchstart', onDragStart, { passive: true });
    el.addEventListener('touchmove', onDragMove, { passive: false });
    el.addEventListener('touchend', onDragEnd);
    el.addEventListener('touchcancel', onDragEnd);
  }

  addBtn.addEventListener('click', () => void submit(idInput.value));

  document.body.append(overlay);
  // Next frame: flip to the open state so the scrim fades and the sheet slides up.
  requestAnimationFrame(() => overlay.classList.add('is-open'));
  // Camera opens straight away — one less tap than a separate "scan" step.
  if (scanQrSupported()) startScan();
}
